<script>
  import { onMount } from "svelte";

  import {
    DataTable,
    TextInput,
    Pagination,
  } from "carbon-components-svelte";

  import { settingStores } from "@stores/settingStores.js";
  import GroupCell from "./components/GroupCell.svelte";
  import EditIcon from "./svg/EditIcon.svelte";
  import SearchInput from "./components/SearchInput.svelte";
  import CrossIcon from "./svg/CrossIcon.svelte";
  import BackIcon from "./svg/BackIcon.svelte";
  import Modal from "../../../lib/components/Modal.svelte";
  import MultiSelect from "../../../lib/components/MultiSelect.svelte";

  import Header from "../../../lib/components/Header.svelte";
  import { SectionWrapper } from "paxray-components-svelte";
  import Button from "../../../lib/components/Button.svelte";

  import { fetchWithSession } from "lib/util/fetchWithSession.js";
  import { fly } from "svelte/transition";

  export let embedded = false;

  let headers = [
    { key: "text", value: "Name" },
    { key: "nUsers", value: "n (users)" },
    { key: "users", value: "Users" },
    { key: "actions", value: "Actions" },
  ];
  let deleteTeamModalOpen = false;
  let createTeamModalOpen = false;
  let teamName = "";
  let deleteUserObj = "";
  let searchTeam = undefined;
  let teamid = null;
  let userRole = null;
  let userids = [];
  let deleteTeamInput = "";

  let page = 1;
  let pageSize = 10;

  $: ({ teams, users } = $settingStores);
  $: usersToShow = users?.map((user) => {
    return { id: user.id, text: user.username };
  });
  $: filteredTeams = searchTeam || teams || [];
  $: visibleTeams = filteredTeams.slice((page - 1) * pageSize, page * pageSize);
  const { getTeams, updateTeam, addTeam, removeTeam } = settingStores;

  const onRemove = (row, cell) => {
    deleteUserObj = row;
    deleteTeamModalOpen = true;
  };

  const onGroupEdit = (row, cell, group) => {
    console.log("onGroupEdit", row, cell, group);
  };

  const onSearch = (value) => {
    page = 1;
    if (teams && value.detail) {
      const searchTerm = value.detail.toLowerCase();
      const filteredTeams = teams.filter((team) => {
        return (
          (team.text && team.text.toLowerCase().includes(searchTerm)) ||
          (Array.isArray(team.users) &&
            team.users.some(
              (user) =>
                typeof user === "string" &&
                user.toLowerCase().includes(searchTerm),
            ))
        );
      });
      searchTeam = filteredTeams;
    } else {
      searchTeam = undefined;
    }
  };

  const confirmDeleteTeam = () => {
    fetchWithSession(
      `/api/authentication/settings/teams/${deleteUserObj?.id}`,
      {
        method: "DELETE",
      },
    )
      .then((res) => res.json())
      .then((res) => {
        if (res.ok) {
          removeTeam(deleteUserObj?.id);
        }
        deleteTeamModalOpen = false;
      });
  };

  const createTeam = async () => {
    try {
      const response = await fetchWithSession("/api/authentication/settings/teams", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: teamName,
          users: userids,
        }),
      });
      const responseText = await response.text();
      let data;

      try {
        data = JSON.parse(responseText);
      } catch {
        throw new Error(`Team konnte nicht erstellt werden (${response.status}).`);
      }

      if (data.ok) {
        createTeamModalOpen = false;
        addTeam({
          id: data.data.id,
          text: data.data.name,
          users: data.data.users.map((user) => ({
            email: user.email,
            id: user.id,
            name: user.username,
          })),
          nUsers: data.data?.users?.length || 0,
        });
      }
    } catch (error) {
      console.error(error);
    }
  };

  const teamUpdate = () => {
    if (userRole === "defaultRole") return;
    fetchWithSession("/api/authentication/settings/teams/" + teamid, {
      method: "PATCH",
      body: JSON.stringify({
        name: teamName,
        users: userids,
      }),
    })
      .then((response) => response.json())
      .then((data) => {
        createTeamModalOpen = false;
        if (data.ok) {
          updateTeam({
            ...data.data,
            text: data.data.name,
            nUsers: data.data.users.length,
            users: data.data.users.map((user) => {
              return { email: user.email, id: user.id, name: user.username };
            }),
          });
        }
      });
  };

  const handleOpenEditModal = (row) => {
    teamName = row.text;
    teamid = row.id;
    userids = row.users.map((d) => d.id);
    createTeamModalOpen = true;
  };

  const handleOpenCreateModal = () => {
    userRole = "";
    userids = [];
    teamName = "";
    teamid = null;
    createTeamModalOpen = true;
  };

  onMount(() => {
    if (!teams || teams.length === 0 || !teams[0]?.users) getTeams();
  });
</script>

{#if !embedded}
  <Header section="Team Management" area="Settings" areaHref="/app/settings/" breadcrumb="Team Management">
    <SearchInput placeholder="Search teams..." on:search={onSearch} />
    <Button on:click={() => handleOpenCreateModal()} variant="secondary" iconLeft={true} setting={true} text="Add Team" icon="plus" />
  </Header>
{/if}

<SectionWrapper fullscreen shrinkContent {embedded}>
<section slot="main">
  {#if embedded}
    <div class="embedded-toolbar"><SearchInput placeholder="Search teams..." on:search={onSearch} /><Button on:click={() => handleOpenCreateModal()} variant="secondary" iconLeft={true} setting={true} text="Add Team" icon="plus" /></div>
  {/if}
  <div>
    {#if teams.length}
      <DataTable
        {headers}
        rows={visibleTeams}
        class="custom-table"
      >
        <svelte:fragment slot="cell" let:row let:cell>
          {#if cell.key === "users"}
            <GroupCell {row} {cell} {onGroupEdit} />
          {:else if cell.key === "actions"}
            <div class="actions-cell">
              <button on:click={() => handleOpenEditModal(row)} class="edit-remove-btn"><EditIcon /></button>
              <button on:click={() => onRemove(row, cell)} class="edit-remove-btn"><img src="/img/ui_icons/delete_forever.svg" alt="Delete" /></button>
            </div>
          {:else}
            {cell?.value || "-"}
          {/if}
        </svelte:fragment>
      </DataTable>
      <Pagination bind:page bind:pageSize totalItems={filteredTeams.length} pageSizes={[10, 25, 50]} />
    {/if}
  </div>
</section>
</SectionWrapper>


<Modal
  actions
  preventCloseOnClickOutside
  size="lg"
  bind:open={deleteTeamModalOpen}
  modalHeading={`Delete Team ${deleteUserObj?.text}`}
  primaryButtonText="Delete"
  primaryButtonAction={confirmDeleteTeam}
  width={653}
  autowidth
  on:close={() => (deleteTeamInput = "")}
>
  <div class="deleteModalBody">
    Are you sure you want to delete the team: <span class="team-name"
      >{deleteUserObj?.text}</span
    >?
    <br />
    <span>This action cannot be undone.</span>
  </div>
</Modal>

<Modal
  size="sm"
  preventCloseOnClickOutside
  bind:open={createTeamModalOpen}
  modalHeading={teamid ? `Edit Team:${teamName}` : "Create Team"}
  actions
  width={653}
  autowidth
  primaryButtonText={teamid ? "Save" : "Create Team"}
  primaryButtonAction={teamid ? teamUpdate : createTeam}
  primaryButtonNotAllow={!teamName}
>
  <div>
    <TextInput
      labelText="Team name"
      bind:value={teamName}
      invalid={!teamName}
      invalidText="Please add a name."
    />
    {#key teamid}
      <MultiSelect
        bind:selectedIds={userids}
        items={usersToShow}
        labelText="User(s)"
        label="User"
      />
    {/key}
  </div>
</Modal>

<style>
  section {
    width: 100%;
  }
  .embedded-toolbar { display: flex; align-items: center; width: 100%; gap: var(--control-gap); margin-bottom: var(--control-gap) !important; }
  .embedded-toolbar :global(.search-input) { flex: 1; min-width: 0; border-color: var(--pax-black); }
  .edit-remove-btn {
    background: var(--pax-black);
    width: 30px;
    height: 30px;
    border-radius: 4px;
    display: grid;
    place-items: center;
  }
  .actions-cell { display: flex; align-items: center; gap: 0.35rem; }
  .actions-cell img { width: 15px; height: 15px; }
  :global(.custom-table th:last-child),
  :global(.custom-table td:last-child) { width: 1%; white-space: nowrap; text-align: left; }
  :global(.cross-plus-icon) {
    rotate: 45deg;
  }
  .deleteModalBody {
    padding: 23px 0px 23px 0px;
    font-family: Inter;
    font-size: 14px;
  }
  .deleteModalBody span {
    font-weight: 700;
  }
  .deleteModalBody :last-child {
    color: var(--pax-orange);
  }
</style>
